import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, CreditCard, Payment } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import RequestManager from '../utilities/requestManager.js';
import { StateManager } from '../utilities/stateManager.js';
import AddCardModal from './AddCardModal.js';

const stripeKey = process.env.NODE_ENV === 'development'
  ? 'pk_test_TKhGrmB7K2upBFGy2x7KSP6e0002WFRtWs'
  : 'pk_live_ItQHT80SBCB2LpW3zkZW84sP00RnGbxWjs';
const stripePromise = loadStripe(stripeKey);

const devtype = process.env.NODE_ENV === 'development' ? 'dev' : 'prod';

const brandLabel = (brand) => {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const expString = (m, y) => `${String(m).padStart(2, '0')}/${String(y).slice(-2)}`;

export default function SavedCardsList(props) {
  const {
    customer = {},
    mode = 'manage',
    defaultAmount = 0,
    chargeContext = {},
    onChargeSuccess,
    title = 'Saved Cards',
  } = props;

  const [cards, setCards] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);
  const [resolvedStripeId, setResolvedStripeId] = React.useState(customer && customer.stripe_id);
  const [amountInput, setAmountInput] = React.useState(
    defaultAmount > 0 ? Number(defaultAmount).toFixed(2) : ''
  );

  React.useEffect(() => {
    setAmountInput(defaultAmount > 0 ? Number(defaultAmount).toFixed(2) : '');
  }, [defaultAmount]);

  React.useEffect(() => {
    setResolvedStripeId(customer && customer.stripe_id);
  }, [customer && customer.stripe_id]);

  const amount = parseFloat(amountInput);
  const stripeId = resolvedStripeId;

  const refresh = React.useCallback(async (idOverride) => {
    const target = idOverride || stripeId;
    if (!target) {
      setCards([]);
      return;
    }
    setLoading(true);
    const res = await RequestManager.post({
      function: 'listPaymentMethods',
      variables: { params: { customer: target, devtype } },
    });
    setLoading(false);
    if (res && res.success) setCards(res.cards || []);
    else setCards([]);
  }, [stripeId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleRemove = async (cardId) => {
    if (!window.confirm('Remove this card from file?')) return;
    StateManager.setLoading(true);
    const res = await RequestManager.post({
      function: 'detachPaymentMethod',
      variables: { params: { payment_method: cardId, devtype } },
    });
    StateManager.setLoading(false);
    if (res && res.success) {
      StateManager.setAlertAndOpen('Card removed.', 'success');
      refresh();
    } else {
      StateManager.setAlertAndOpen(res && res.message ? `Remove failed: ${res.message}` : 'Failed to remove card.', 'error');
    }
  };

  const handleCharge = async (card) => {
    if (!amount || amount < 1) {
      StateManager.setAlertAndOpen('Enter an amount greater than $0 first.', 'error');
      return;
    }
    if (!window.confirm(`Charge ${brandLabel(card.brand)} ending in ${card.last4} for $${Number(amount).toLocaleString(undefined, {minimumFractionDigits: 2})}?`)) return;

    StateManager.setLoading(true);
    const params = {
      customer: stripeId,
      payment_method: card.id,
      amount: Math.floor(Number(amount) * 100),
      devtype,
      description: chargeContext.description,
      metadata: {
        type: chargeContext.type,
        reference: chargeContext.reference,
      },
      receipt_email: customer.email || undefined,
    };
    const res = await RequestManager.post({
      function: 'chargeSavedCard',
      variables: { params },
    });

    if (res && res.success) {
      StateManager.setLoading(false);
      StateManager.setAlertAndOpen('Card charged successfully.', 'success');
      if (onChargeSuccess) onChargeSuccess(res.paymentIntent);
      return;
    }

    if (res && res.requires_action && res.client_secret) {
      const stripe = await stripePromise;
      const sca = await stripe.confirmCardPayment(res.client_secret);
      StateManager.setLoading(false);
      if (sca.error) {
        StateManager.setAlertAndOpen(`Authentication failed: ${sca.error.message}`, 'error');
        return;
      }
      if (sca.paymentIntent && sca.paymentIntent.status === 'succeeded') {
        StateManager.setAlertAndOpen('Card charged successfully.', 'success');
        if (onChargeSuccess) onChargeSuccess(sca.paymentIntent);
        return;
      }
      StateManager.setAlertAndOpen('Charge did not complete. Try another card.', 'error');
      return;
    }

    StateManager.setLoading(false);
    const msg = (res && res.message) || 'Charge failed.';
    StateManager.setAlertAndOpen(`Charge failed: ${msg}`, 'error');
  };

  // A card can only be saved if there's a customer record to attach it to —
  // AddCardModal will create the Stripe customer on the fly when stripe_id is missing.
  const canAddCard = !!(customer && customer.id);

  if (!stripeId && mode !== 'manage' && !canAddCard) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No customer is attached to this order, so a card can't be saved on file.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1"><b>{title}</b></Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Add />}
          disabled={!canAddCard}
          onClick={() => setAddOpen(true)}
        >
          Add card on file
        </Button>
      </Box>

      {mode === 'charge' && cards.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
          <TextField
            label="Amount to charge"
            value={amountInput}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
              setAmountInput(raw);
            }}
            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { fontSize: '1.25rem', fontWeight: 600 },
            }}
            sx={{ width: 200 }}
          />
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && cards.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
          No saved cards.
        </Typography>
      )}

      {!loading && cards.length > 0 && (
        <List dense>
          {cards.map(card => (
            <ListItem key={card.id} divider>
              <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />
              <ListItemText
                primary={`${brandLabel(card.brand)} •••• ${card.last4}`}
                secondary={`Exp ${expString(card.exp_month, card.exp_year)}`}
              />
              <ListItemSecondaryAction>
                {mode === 'charge' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<Payment />}
                    onClick={() => handleCharge(card)}
                    disabled={!amount || amount < 1}
                    sx={{ mr: 1 }}
                  >
                    Charge
                  </Button>
                )}
                {mode === 'manage' && (
                  <IconButton edge="end" onClick={() => handleRemove(card.id)} title="Remove">
                    <Delete />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {addOpen && (
        <AddCardModal
          customer={customer}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSuccess={(result) => {
            setAddOpen(false);
            const newId = result && result.stripeId;
            if (newId) setResolvedStripeId(newId);
            refresh(newId);
          }}
        />
      )}
    </Box>
  );
}
